import type { IRemoteModuleManifest } from '../types';
import type { IContainerEntry /*, IShareScope*/ } from '../RemoteRuntime';
import type RemoteRuntime from '../RemoteRuntime';
import { getContainerName } from '../utils/get-container-name';
import { ensureExternalsPrepared } from '../../progressive/external-getter';
import { getBestPublicPath } from '../utils/get-best-public-path';

declare var __webpack_init_sharing__: (scope: string) => Promise<void>;
declare const __webpack_share_scopes__: Record<
  string,
  Record<string, { loaded?: 1; get: () => Promise<unknown>; from: string; eager: boolean }>
>;

declare global {
  interface Window {
    __support_dynamic_import?: boolean;
  }
}

enum REMOTE_MODULE_STATE {
  DECLARED, // 被声明使用到，初始状态
  REGISTERED, // 描述信息已注册
  ENTRY_LOADED, // entry 资源已加载
  INITIALIZED, // shared 已初始化完成，可以用于加载模块
  UNINSTALLED, // 已卸载，所有信息已清除，预留
}

// 指定 MF share scope，需要在 ModuleFederationPlugin 中配置
// const SHARE_SCOPE: string = 'meego-shared';
const SHARE_SCOPE: string = 'default'; // 简化设计，先用 default
// 记录资源 url 是否被加载过
const urlCache = new Set<string>();
// 记录资源 url 是否被做过预加载
const preloadUrlCache = new Set<string>();

// 脚本加载超时时间
const RESOURCE_TIMEOUT = getResourceTimeout();

function getResourceTimeout() {
  const defaultTimeout = 30000; // 默认30s

  // 从 tcc 读配置
  const tccTimeout = Number(window.remoteLoaderConfig?.resource_timeout);

  // 从 url 参数读配置
  const query = new URLSearchParams(window.location.search);
  const queryTimeout = Number(query.get('remote_module_resource_timeout'));

  if (queryTimeout && !isNaN(queryTimeout) && queryTimeout > 0) {
    return queryTimeout;
  } else if (tccTimeout && !isNaN(tccTimeout) && tccTimeout > 0) {
    return tccTimeout;
  }

  return defaultTimeout;
}

async function fetchWithTimeout(resource: string, options: { timeout?: number } = {}) {
  const { timeout = RESOURCE_TIMEOUT } = options;
  let execFetch;
  let timeoutId: number = 0;

  // 做下兼容保护
  if (typeof AbortController !== undefined) {
    const controller = new AbortController();
    timeoutId = setTimeout(() => {
      controller.abort('timeout');
    }, timeout);
    execFetch = () =>
      fetch(resource, {
        ...options,
        signal: controller.signal,
      }).catch(err => {
        if (err === 'timeout') {
          throw new Error(`fetch timeout: ${resource}`);
        } else {
          throw err;
        }
      });
  } else {
    execFetch = () =>
      new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => {
          reject(`fetch timeout: ${resource}`);
        }, timeout);
        fetch(resource, options as unknown as RequestInit).then(
          res => resolve(res),
          err => reject(err),
        );
      });
  }
  return execFetch().then(
    res => {
      clearTimeout(timeoutId);
      return res;
    },
    err => {
      clearTimeout(timeoutId);
      throw err;
    },
  );
}

/**
 * 提供基于 MF 的 remote module 的底层加载能力
 */
export default class RemoteModuleHandler {
  // container 的状态
  private state: REMOTE_MODULE_STATE;
  // container 名
  private readonly containerName: string;
  // 全局 runtime 实例，需要在 new 的时候传进来
  private readonly runtime: RemoteRuntime;
  // container 的描述文件
  private manifest?: IRemoteModuleManifest;
  /** 使用同步加载 */
  private syncLoad?: boolean;

  constructor(options: { container: string; runtime: RemoteRuntime }) {
    this.containerName = options.container;
    this.runtime = options.runtime;

    const manifest = this.runtime.registry.get(this.containerName);
    if (manifest) {
      this.manifest = manifest;
    }

    // check global registry to determine actual state
    // e.g. entry may have been loaded already
    const { containers, registry } = this.runtime;
    // 根据已加载的数据确定实际状态
    if (containers[this.containerName]) {
      if (containers[this.containerName].initialized) {
        this.state = REMOTE_MODULE_STATE.INITIALIZED;
      } else {
        this.state = REMOTE_MODULE_STATE.ENTRY_LOADED;
      }
    } else if (registry.has(this.containerName)) {
      this.state = REMOTE_MODULE_STATE.REGISTERED;
    } else {
      this.state = REMOTE_MODULE_STATE.DECLARED;
    }
  }

  /** 以下是 transitions like methods，一系列底层方法，会导致状态迁移，总是返回 Promise<RemoteModuleHandler> **/

  // 进行中的 initShared 调用
  private pendingRegister: Promise<RemoteModuleHandler> | null = null;
  /**
   * 尝试获取 manifest，并注册到全局，如果已经加载过，那会直接成功，重复调用安全
   */
  register: () => Promise<RemoteModuleHandler> = async () => {
    const { registry, servicesDiscovered } = this.runtime;

    if (REMOTE_MODULE_STATE.DECLARED === this.state) {
      // 已注册过
      if (registry.has(this.containerName)) {
        return this;
      }
      // 未注册过
      if (servicesDiscovered[this.containerName]) {
        this.pendingRegister =
          this.pendingRegister ||
          new Promise((resolve, reject) => {
            this.pureRegister().then(
              () => resolve(this),
              e => reject(e),
            );
          });
        return this.pendingRegister;
      } else {
        throw new Error(`[RM] no service discovered for ${this.containerName}`);
      }
    } else {
      return this;
    }
  };

  // 进行中的 initShared 调用
  private pendingLoadEntry: Promise<RemoteModuleHandler> | null = null;
  /**
   * 尝试加载 entry js，如果已经加载过，则不会重复加载，重复调用安全
   */
  loadEntry = async (): Promise<RemoteModuleHandler> => {
    if (REMOTE_MODULE_STATE.REGISTERED === this.state) {
      this.pendingLoadEntry =
        this.pendingLoadEntry ||
        new Promise((resolve, reject) => {
          this.pureLoadEntry().then(
            () => resolve(this),
            e => reject(e),
          );
        });
      return this.pendingLoadEntry;
    } else if (
      [REMOTE_MODULE_STATE.DECLARED, REMOTE_MODULE_STATE.UNINSTALLED].includes(this.state)
    ) {
      throw new Error(`[RM] remote entry not registered for ${this.containerName}`);
    } else if (!this.runtime?.containers?.[this.containerName]) {
      throw new Error(
        `[RM] remote entry state not match, no container found for ${this.containerName}, current state is ${this.state}`,
      );
    } else {
      return this;
    }
  };

  // 进行中的 initShared 调用
  private pendingInitShared: Promise<RemoteModuleHandler> | null = null;
  /**
   * 尝试给已经加载 MF container 初始化 shared，重复调用安全
   */
  initShared = async (): Promise<RemoteModuleHandler> => {
    if (REMOTE_MODULE_STATE.ENTRY_LOADED === this.state) {
      const { containers } = this.runtime;
      const container = containers[this.containerName];

      if (!container) {
        throw new Error(`[RM] entry not loaded, cannot initShared for ${this.containerName}`);
      }

      // 已初始化过
      if (container?.initialized) {
        return this;
      }

      this.pendingInitShared =
        this.pendingInitShared ||
        new Promise((resolve, reject) => {
          this.pureInitShared().then(
            () => resolve(this),
            e => reject(e),
          );
        });
      return this.pendingInitShared;
    } else if (REMOTE_MODULE_STATE.INITIALIZED === this.state) {
      return this;
    } else {
      throw new Error(`[RM] entry not loaded, cannot initShared for ${this.containerName}`);
    }
  };
  uninstall = async (): Promise<RemoteModuleHandler> => {
    // TODO 预留给以后，清除所有已加载的 registry、entry、chunk
    return Promise.resolve(this);
  };

  /** 加载模块 **/
  loadChunk = async (module: string): Promise<unknown> => {
    if (REMOTE_MODULE_STATE.INITIALIZED === this.state) {
      return this.pureLoadChunk(module);
    } else {
      await this.register();
      await this.loadEntry();
      await this.initShared();
      return this.pureLoadChunk(module);
    }
  };

  /** 预加载 entry js **/
  preloadEntry = () => {
    if (this.manifest?.internalGetter) {
      return this;
    } else if (this.manifest?.remoteEntry?.url) {
      const url = this.manifest?.remoteEntry?.url;

      if (preloadUrlCache.has(url)) {
        return this;
      }

      const preloadLink = document.createElement('link');
      preloadLink.href = url;
      preloadLink.rel = 'preload';
      preloadLink.as = 'script';
      document.head.appendChild(preloadLink);

      preloadUrlCache.add(url);

      return this;
    } else {
      throw new Error(`[RM] no remote entry for ${this.containerName}`);
    }
  };

  // 预加载 chunk js
  // preloadChunk = (module: string) => {
  //   // TODO 根据 manifest 中定义的模块，预加载其 chunk js
  // };

  /** 以下是一些内部方法，通常是只需要执行一次的方法，抽出来方便缓存 **/
  // register 的内部实现
  private pureRegister = async () => {
    const { registry, servicesDiscovered } = this.runtime;

    if (servicesDiscovered[this.containerName]) {
      return fetch(servicesDiscovered[this.containerName])
        .then(response => {
          if (!response.ok) {
            throw new Error(
              `[RM] HTTP error for ${this.containerName}! Status: ${response.status}`,
            );
          }
          return response.json() as unknown as IRemoteModuleManifest;
        })
        .then(manifest => {
          // 注册到 registry 上
          registry.register(getContainerName(manifest, this.containerName), manifest);
          this.manifest = manifest;
          this.state = REMOTE_MODULE_STATE.REGISTERED;
          return this;
        });
    }
    throw new Error(`[RM] no service discovered for ${this.containerName}`);
  };

  // loadEntry 的内部实现
  private pureLoadEntry = async () => {
    return new Promise<RemoteModuleHandler>((resolve, reject) => {
      const manifestName = this.manifest?.name;
      if (this.manifest?.internalGetter) {
        // CASE 1: 未独立打包的模拟 remote-entry，调用声明的 internalGetter 方法
        this.manifest
          ?.internalGetter()
          .then((res: { default: IContainerEntry }) => {
            if (!res.default) {
              throw new Error(
                `[RM] internalGetter returns no container for ${this.containerName}, name = ${manifestName}`,
              );
            }
            this.runtime.containers[this.containerName] = res.default;
            this.state = REMOTE_MODULE_STATE.ENTRY_LOADED;
            resolve(this);
          })
          .catch(e => {
            reject(e);
          });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } else if (manifestName && window[manifestName]) {
        // CASE 2: 此时意味着，有RM之外的机制（RM 会清除），往全局挂载了 MF 模块，此时直接复用，且不删除

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const container = window[manifestName] as IContainerEntry;
        this.runtime.containers[this.containerName] = container;
        this.state = REMOTE_MODULE_STATE.ENTRY_LOADED;
        resolve(this);
      } else if (this.manifest?.remoteEntry?.url) {
        // CASE 3: 已独立打包的 remote-entry 有 url，用 script 加载
        const { url } = this.manifest.remoteEntry;

        // 已加载过
        if (urlCache.has(url)) {
          resolve(this);
          return;
        }

        // 通用的加载后续处理逻辑
        const onScriptLoad = () => {
          urlCache.add(url);
          /**
           * mf 代码会把产物挂在到 window 上，用的是配置中的 name，这个 name 有一些限制，因此我们只用于加载，name 一定存在
           * 实际使用时，会使用 this.containerName 作为 key，从 runtime 中获取
           */
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const container = window[manifestName] as IContainerEntry;

          // 出错处理
          if (!container) {
            // 特殊case，onload 进入了2次及以上，上一次已经初始化好
            if (
              this.runtime.containers[this.containerName] &&
              this.state === REMOTE_MODULE_STATE.ENTRY_LOADED
            ) {
              resolve(this);
              return;
            } else {
              // 真异常场景

              // 尝试找到 script 报错的信息
              const scriptErrorIndex = this.runtime.scriptErrors.findLastIndex(({ filename }) =>
                filename?.includes(url),
              );
              if (scriptErrorIndex >= 0) {
                const scriptError = this.runtime.scriptErrors[scriptErrorIndex];
                // 清理更早的错误
                this.runtime.scriptErrors.splice(0, scriptErrorIndex + 1);
                reject(
                  new Error(
                    `[RM] entry script throws error for ${
                      this.containerName
                    }, name = ${manifestName}, script error = ${JSON.stringify(scriptError)}`,
                  ),
                );
              } else {
                reject(
                  new Error(
                    `[RM] entry script returns no container for ${
                      this.containerName
                    }, name = ${manifestName}, url = ${url}, state = ${
                      this.state
                    }, container init = ${!!this.runtime.containers[this.containerName]}`,
                  ),
                );
              }
              return;
            }
          }

          this.runtime.containers[this.containerName] = container;
          try {
            if (!this.manifest?.skipClear) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              delete window[manifestName];
            }
          } catch (e) {
            reject(
              new Error(
                `[RM] window library target clear fail for ${
                  this.containerName
                }, name = ${manifestName}, url = ${url}, state = ${
                  this.state
                }, container init = ${!!this.runtime.containers[this.containerName]}`,
              ),
            );
          }
          this.state = REMOTE_MODULE_STATE.ENTRY_LOADED;
          resolve(this);
        };
        const onScriptError = (e: Event | string) => {
          // 如果 script 加载失败，尝试删除 SW 缓存
          if (window.__SERVICE_WORKER_MANAGER__) {
            window.__SERVICE_WORKER_MANAGER__.deleteResourceCache(url);
          }
          reject(e);
        };

        // 使用 script 标签加载资源
        const loadScript = (url: string) => {
          if (window.__support_dynamic_import) {
            let timer = setTimeout(() => {
              onScriptError({
                type: 'timeout',
                url,
              } as unknown as Event);
            }, RESOURCE_TIMEOUT);
            const loadModule = new Function('url', 'return import(url)');
            loadModule(url.startsWith('//') ? location.protocol + url : url)
              .then(() => {
                clearTimeout(timer);
                onScriptLoad();
              })
              .catch(onScriptError);
          } else {
            const element = document.createElement('script');
            element.src = url;
            element.type = 'text/javascript';
            if (!this.syncLoad) {
              element.async = true;
            }
            if (0 !== element.src.indexOf('//' + window.location.host + '/')) {
              element.crossOrigin = 'anonymous';
            }
            const scriptCommonCallback = (
              callback?: (e?: Event | string) => void,
              event?: Event | string,
            ) => {
              element.onload = null;
              element.onerror = null;
              clearTimeout(timoutNum);
              element.parentNode?.removeChild(element);
              callback?.(event);
            };
            let timoutNum = setTimeout(() => {
              scriptCommonCallback(onScriptError, {
                type: 'timeout',
                target: element,
                url,
              } as unknown as Event);
            }, RESOURCE_TIMEOUT);

            element.onload = e => {
              scriptCommonCallback(onScriptLoad, e);
            };
            element.onerror = e => {
              scriptCommonCallback(onScriptError, e);
            };
            document.head.appendChild(element);
          }
        };

        const { bestPublicPath, currentPublicPath } = getBestPublicPath(this.manifest);

        // CASE 3.1 新逻辑：子仓的打包产物 publicPath 是 auto，直接使用 script 加载 entry.js
        if (this.manifest?.useAutoPublicPath) {
          if (bestPublicPath && currentPublicPath) {
            // CASE 3.1.2 存在 CDN 选路，直接替换 entry.js 路由的前缀, 内部 chunk 加载会自动替换成 entry 的同域名
            const newUrl = url.replace(currentPublicPath, bestPublicPath);
            loadScript(newUrl);
          } else {
            // CASE 3.1.1 不存在 CDN 选路，直接使用 script 加载 entry.js
            loadScript(url);
          }
        } else {
          // CASE 3.2 旧逻辑：子仓的打包产物 publicPath 是手动写死的，这种场景需要用 fetch 加载 entry.js，再替换 script 内部的路由
          // CASE 3.2.1 存在选路逻辑
          if (bestPublicPath && currentPublicPath) {
            fetchWithTimeout(url.replace(currentPublicPath, bestPublicPath))
              .then(res => res.text())
              .then(text => {
                const element = document.createElement('script');
                element.type = 'text/javascript';
                element.textContent = text.replaceAll(currentPublicPath, bestPublicPath);
                document.head.appendChild(element);
                onScriptLoad();
              }, onScriptError)
              .catch(onScriptError);
          } else {
            // CASE 3.2.2 不存在选路逻辑, 使用 script 加载
            loadScript(url);
          }
        }
      } else {
        // CASE else：没找到任何加载的入口
        reject(new Error(`[RM] no remote entry for ${this.containerName}`));
        return;
      }
    });
  };
  // initShared 的内部实现
  private pureInitShared = async () => {
    const { containers } = this.runtime;
    const container = containers[this.containerName];

    // TODO container 级别的 progressiveExternals 可以在这个阶段进行初始化

    // Initializes the share scope. This fills it with known provided modules from this build and all remotes
    await __webpack_init_sharing__(SHARE_SCOPE);
    // Initialize the container, it may provide shared modules
    await container.init(__webpack_share_scopes__[SHARE_SCOPE]);
    // 手动记录状态，让新建的 handler 可以获取到
    container.initialized = true;

    this.state = REMOTE_MODULE_STATE.INITIALIZED;
  };

  // loadChunk 的内部实现
  private pureLoadChunk = async (module: string) => {
    const { exposesInfo } = this.manifest!;

    // 如果有声明的依赖，提前加载好
    if (exposesInfo && exposesInfo[module]) {
      const { progressiveExternals = {} } = exposesInfo[module];
      await ensureExternalsPrepared(
        Object.values(progressiveExternals).map(path => {
          // 取 path 最后部分作为 progressiveExternal
          if (Array.isArray(path) && path.length) {
            return path[path.length - 1];
          } else if (typeof path === 'string') {
            const parsed = (path as string).split('.');
            if (parsed.length) {
              return parsed[parsed.length - 1];
            }
          }
          return String(path as unknown);
        }),
      );
    }

    const { containers } = this.runtime;
    const container = containers[this.containerName];
    const factory = await container.get(module);
    const Module = factory();
    return Module as unknown;
  };

  /** 对外 getter **/
  public getManifest = () => {
    return this.manifest;
  };
  public getContainer = () => {
    // const { containers } = this.runtime;
    // const container = containers[this.containerName];
    /**
     * 需要手动构建这个 container 结构的原因是，
     * 由于主仓库使用了 external promise（external 的自动挡），导致部分模块变成异步模块
     * webpack 默认的实现不会 await 异步模块的加载，需要手动在中间 await 一下
     */
    return {
      get: async (module: string) => {
        const chunkModule = await this.loadChunk(module);
        // 下面这个 function 需要是同步的
        return () => chunkModule;
      },
      init: (/*shareScope: IShareScope, initScope: Array<unknown>*/) => {
        // 留给 webpack 自己去控制 init 的时机
        // try {
        //   return container.init(shareScope, initScope);
        // } catch (e) {
        //   // console.log('remote container already initialized');
        //   return container;
        // }
      },
    };
  };

  public setSyncLoad() {
    this.syncLoad = true;
  }
}
